import { Button, Link, Popover, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useDeleteDeliveryOptionMutation,
  useFindAllDeliveryOptionsQuery,
} from "@/libs/generated/hooks";
import type { FindAllDeliveryOptionsQuery } from "@/libs/generated/operations";

type OriginalDeliveryOptionRecord = FindAllDeliveryOptionsQuery["findAllDeliveryOptions"][number];

type DeliveryOptionRecord = Omit<OriginalDeliveryOptionRecord, "requiresPhoto"> & {
  requiresFile: boolean;
  fileType: "IMAGE" | "PDF" | null;
};

interface DeleteOptionPopoverProps {
  anchorEl: HTMLElement | null;
  record: DeliveryOptionRecord | null;
  onClose: () => void;
  onEdit: (record: DeliveryOptionRecord) => void;
}

export function DeleteOptionPopover({
  anchorEl,
  record,
  onClose,
  onEdit,
}: DeleteOptionPopoverProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const deleteMutation = useDeleteDeliveryOptionMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: useFindAllDeliveryOptionsQuery.getKey() });
      onClose();
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      if (msg.toLowerCase().includes("restrict") || msg.toLowerCase().includes("references")) {
        setError(t("deliveryOption.delete.error"));
      } else {
        setError(msg);
      }
    },
  });

  const handleDelete = () => {
    if (!record) return;
    setError(null);
    deleteMutation.mutate({ id: record.id });
  };

  const handleDeactivateInstead = () => {
    if (!record) return;
    onClose();
    onEdit({ ...record, active: false });
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: { p: 2, maxWidth: 260 } }}
    >
      <Typography variant="body2" fontWeight={500} mb={0.5}>
        {t("deliveryOption.delete.title", { name: record?.name })}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        {t("deliveryOption.delete.description")}
      </Typography>

      {error && (
        <Typography variant="caption" color="error" display="block" mb={1}>
          {error}
          {error.includes("Deactivate") && (
            <>
              {" "}
              <Link
                component="button"
                variant="caption"
                color="error"
                underline="always"
                onClick={handleDeactivateInstead}
              >
                {t("deliveryOption.delete.deactivateInstead")}
              </Link>
            </>
          )}
        </Typography>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button size="small" onClick={onClose}>
          {t("deliveryOption.delete.cancel")}
        </Button>
        <Button
          size="small"
          color="error"
          variant="contained"
          onClick={handleDelete}
          loading={deleteMutation.isPending}
        >
          {t("deliveryOption.delete.delete")}
        </Button>
      </Stack>
    </Popover>
  );
}
